import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EntityService, SharedService } from '@app/core';
import { DashboardService } from '@modules/dashboard/services';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss']
})
export class OverviewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isTabActive: boolean;
  @Input() duration: { startDate: string, endDate: string, dateField: string };
  previousDuration: { startDate: string, endDate: string, dateField: string };
  statusWiseInvoiceCount = {
    reviewed: 0,
    pending: 0,
    invalid: 0,
    duplicate: 0,
    failed: 0,
    rejected: 0
  };
  documentTypeInvoiceCount = {
    taxInvoice: 0,
    creditNote: 0,
  };
  totalInvoiceCount: any = 0;
  donutSeries: {};
  isSatusWiseSummaryLoading: boolean = true;
  isInvoiceAmountLoading: boolean = true;
  isInvoiceGstLoading: boolean = true;
  isTimeWiseSummaryLoading: boolean = true;
  invoiceAmount: { amount: number, change: number };
  invoiceGst: { gst: number, change: number };
  mixedSeries: { name: string, type: string, data: any[], tax?: { gsts: number[], vats: number[], cesses: number[], excises: number[] } }[];
  mixedCategories: string[];
  mixedYAxisRange: { min: number, max: number }[];
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    public sharedService: SharedService,
    private entityService: EntityService
  ) { }

  ngOnInit(): void {
    this.entityService.entitiesChanges$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if ((this.isTabActive && this.duration?.startDate && this.duration?.endDate) || res.isEntityChanged) {
        this.setPropsAndGetStatusWiseInvoiceCount();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isTabActive && this.duration?.startDate && this.duration?.endDate && (this.duration?.startDate !== this.previousDuration?.startDate || this.duration?.endDate !== this.previousDuration?.endDate || this.duration?.dateField !== this.previousDuration?.dateField)) {
      this.setPropsAndGetStatusWiseInvoiceCount();
    }
  }

  setPropsAndGetStatusWiseInvoiceCount() {
    this.previousDuration = this.duration;
    this.isSatusWiseSummaryLoading = true;
    this.isInvoiceAmountLoading = true;
    this.isInvoiceGstLoading = true;
    this.isTimeWiseSummaryLoading = true;
    this.getStatusWiseInvoiceCount();
  }

  get isChartEmpty() { return !this.mixedYAxisRange.reduce((sum, range) => sum + range.min + range.max, 0); }

  getStatusWiseInvoiceCount() {
    this.isSatusWiseSummaryLoading = true;
    this.dashboardService.dashboardOverview(this.duration).subscribe((res) => {

      this.statusWiseInvoiceCount = {
        reviewed: res.statusWiseInvoiceCount.statuses.reviewed,
        pending: res.statusWiseInvoiceCount.statuses.uploaded + res.statusWiseInvoiceCount.statuses.inprocess + res.statusWiseInvoiceCount.statuses.processed + res.statusWiseInvoiceCount.statuses.draft,
        invalid: res.statusWiseInvoiceCount.statuses.rejected + res.statusWiseInvoiceCount.statuses.failed + res.statusWiseInvoiceCount.statuses.duplicate,
        rejected: res.statusWiseInvoiceCount.statuses.rejected,
        failed: res.statusWiseInvoiceCount.statuses.failed,
        duplicate: res.statusWiseInvoiceCount.statuses.duplicate,
      };

      this.documentTypeInvoiceCount = res.statusWiseInvoiceCount.documentTypes;

      this.totalInvoiceCount = Object.values(res.statusWiseInvoiceCount.statuses).reduce((sum: number, count: number) => sum + count, 0);

      this.donutSeries = {
        "Invoices Reviewed": res.statusWiseInvoiceCount.statuses.reviewed,
        "Invoices Pending": res.statusWiseInvoiceCount.statuses.uploaded + res.statusWiseInvoiceCount.statuses.inprocess + res.statusWiseInvoiceCount.statuses.processed + res.statusWiseInvoiceCount.statuses.draft,
        "Invoices Rejected": res.statusWiseInvoiceCount.statuses.rejected + res.statusWiseInvoiceCount.statuses.failed + res.statusWiseInvoiceCount.statuses.duplicate
      };

      this.isSatusWiseSummaryLoading = false;
      this.isInvoiceAmountLoading = false;
      this.isInvoiceGstLoading = false;
      this.isTimeWiseSummaryLoading = false;


      this.invoiceAmount = res.invoiceAmount;
      this.invoiceGst = res.invoiceGst;

      this.getTimeWiseInvoice(res.timeWiseSummary);
    }, (error: any) => {
      this.sharedService.errorAlert(error);
      this.isSatusWiseSummaryLoading = false;
    });
  }

  // getInvoiceAmount() {
  //   this.isInvoiceAmountLoading = true;
  //   this.dashboardService.getInvoiceAmount(this.duration).subscribe(
  //     (res) => {
  //       this.invoiceAmount = res;
  //       this.isInvoiceAmountLoading = false;
  //       this.getInvoiceGST();
  //     },
  //     (error: any) => {
  //       this.sharedService.errorAlert(error);
  //       this.isInvoiceAmountLoading = false;
  //     }
  //   );
  // }

  // getInvoiceGST() {
  //   this.isInvoiceGstLoading = true;
  //   this.dashboardService.getInvoieGst(this.duration).subscribe(
  //     (res) => {
  //       this.invoiceGst = res;
  //       this.isInvoiceGstLoading = false;
  //       this.getTimeWiseInvoice();
  //     },
  //     (error: any) => {
  //       this.sharedService.errorAlert(error);
  //       this.isInvoiceGstLoading = false;
  //     }
  //   );
  // }

  getTimeWiseInvoice(res) {
    const timeFrame = Object.keys(res)?.[0];
    this.mixedCategories = res[timeFrame].map(stat => stat[timeFrame]);
    const invoiceAmounts = res[timeFrame].map(stat => stat.amount);
    const invoiceTaxes = res[timeFrame].map(stat => stat.gst + stat.vat + stat.cess + stat.excise);
    const gsts = res[timeFrame].map(stat => stat.gst);
    const vats = res[timeFrame].map(stat => stat.vat);
    const cesses = res[timeFrame].map(stat => stat.cess);
    const excises = res[timeFrame].map(stat => stat.excise);
    const invoiceCounts = res[timeFrame].map(stat => stat.count);
    this.mixedSeries = [
      {
        name: "Payable Amount",
        type: "bar",
        data: invoiceAmounts
      },
      {
        name: "Taxes",
        type: "bar",
        data: invoiceTaxes,
        tax: { gsts, vats, cesses, excises }
      },
      {
        name: "No. of Invoices Reviewed",
        type: "line",
        data: invoiceCounts
      }
    ];
    this.mixedYAxisRange = [
      {
        min: Math.min(...invoiceAmounts, ...invoiceTaxes),
        max: Math.max(...invoiceAmounts, ...invoiceTaxes)
      },
      {
        min: Math.min(...invoiceAmounts, ...invoiceTaxes),
        max: Math.max(...invoiceAmounts, ...invoiceTaxes)
      }
    ];

    // this.dashboardService.getTimeWiseInvoice(this.duration).subscribe(
    //   (res) => {
    //   },
    //   (error: any) => {
    //     this.sharedService.errorAlert(error);
    //     this.isTimeWiseSummaryLoading = false;
    //   }
    // );
  }

  mixedCustomTooltip = ({ series, seriesIndex, dataPointIndex, w }) => {
    const { seriesNames, categoryLabels, initialSeries } = w.globals;

    return '<div class="arrow_box">' + '<div class="apexcharts-tooltip-title apxcharts-active">' + categoryLabels[dataPointIndex] + '</div>' +
      '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex;">' +
      '<span class="apexcharts-tooltip-marker" style="background-color: rgb(253, 127, 111); display: block;"></span>' +
      '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; display: block;">' +
      '<div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">' + seriesNames[0] + ': </span>' +
      '<span class="apexcharts-tooltip-text-y-value">' + this.sharedService.formatNumber(series[0][dataPointIndex], true, 0, 2, true) + '</span></div>' +
      '<div class="apexcharts-tooltip-goals-group"><span class="apexcharts-tooltip-text-goals-label"></span>' +
      '<span class="apexcharts-tooltip-text-goals-value"></span></div>' +
      '<div class="apexcharts-tooltip-z-group"><span class="apexcharts-tooltip-text-z-label"></span>' +
      '<span class="apexcharts-tooltip-text-z-value"></span></div></div></div>' +
      '<div class="apexcharts-tooltip-series-group khr-taxes-group" style="order: 2; display: flex;">' +
      '<span class="apexcharts-tooltip-marker" style="background-color: #ebb901; display: block;"></span>' +
      '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; display: block;">' +
      '<div class="apexcharts-tooltip-y-group pb-0">' +
      '<span class="apexcharts-tooltip-text-y-label">' + seriesNames[1] + ': </span>' +
      '<span class="apexcharts-tooltip-text-y-value">' + this.sharedService.formatNumber(series[1][dataPointIndex], true, 0, 2, true) + '</span>' +
      '</div>' +
      '<div class="d-flex khr-taxes-list">' +
      '<div>' +
      '<p class="apexcharts-tooltip-text-y-label mb-0">GST</p>' +
      '<p class="apexcharts-tooltip-text-y-value mb-0">' + this.sharedService.formatNumber(initialSeries[1].tax.gsts[dataPointIndex], true, 0, 2, true) + '</p>' +
      '</div>' +
      '<div>' +
      '<p class="apexcharts-tooltip-text-y-label mb-0">VAT</p>' +
      '<p class="apexcharts-tooltip-text-y-value mb-0">' + this.sharedService.formatNumber(initialSeries[1].tax.vats[dataPointIndex], true, 0, 2, true) + '</p>' +
      '</div>' +
      '<div>' +
      '<p class="apexcharts-tooltip-text-y-label mb-0">CESS</p>' +
      '<p class="apexcharts-tooltip-text-y-value mb-0">' + this.sharedService.formatNumber(initialSeries[1].tax.cesses[dataPointIndex], true, 0, 2, true) + '</p>' +
      '</div>' +
      '<div>' +
      '<p class="apexcharts-tooltip-text-y-label mb-0">Excise</p>' +
      '<p class="apexcharts-tooltip-text-y-value mb-0">' + this.sharedService.formatNumber(initialSeries[1].tax.excises[dataPointIndex], true, 0, 2, true) + '</p>' +
      '</div>' +
      '</div>' +
      '<div class="apexcharts-tooltip-goals-group">' +
      '<span class="apexcharts-tooltip-text-goals-label"></span>' +
      '<span class="apexcharts-tooltip-text-goals-value"></span>' +
      '</div>' +
      '<div class="apexcharts-tooltip-z-group">' +
      '<span class="apexcharts-tooltip-text-z-label"></span>' +
      '<span class="apexcharts-tooltip-text-z-value"></span>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="apexcharts-tooltip-series-group" style="order: 3; display: flex;">' +
      '<span class="apexcharts-tooltip-marker" style="background-color: rgb(126, 176, 213); display: block;"></span>' +
      '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; display: block;">' +
      '<div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">' + seriesNames[2] + ': </span>' +
      '<span class="apexcharts-tooltip-text-y-value">' + this.sharedService.formatNumber(series[2][dataPointIndex], false, 0, 2, true) + '</span></div>' +
      '<div class="apexcharts-tooltip-goals-group"><span class="apexcharts-tooltip-text-goals-label"></span>' +
      '<span class="apexcharts-tooltip-text-goals-value"></span></div>' +
      '<div class="apexcharts-tooltip-z-group"><span class="apexcharts-tooltip-text-z-label"></span>' +
      '<span class="apexcharts-tooltip-text-z-value"></span></div></div></div></div>';
  }

  ngOnDestroy(): void {
    this.destroy$?.next();
    this.destroy$?.complete();
  }
}